import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";

export default function RecoveryPage() {
  const [forWhom, setForWhom] = useState(null); // "me" or "them"
  const [form, setForm] = useState({ name: "", email: "", phone: "", story: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.story) {
      toast.error("Please fill in your name, email, and your message");
      return;
    }
    setLoading(true);
    try {
      const enquiryType = forWhom === "them" ? "Family Protocol" : "1:1 Recovery Protocol";
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        message: `RECOVERY ENQUIRY — ${enquiryType}\nPhone: ${form.phone}\nFor: ${forWhom === "them" ? "Someone they love" : "Themselves"}\n\n${form.story}`,
      });
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#050505] min-h-screen font-body" data-testid="recovery-page">
      {/* Simple Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="font-brand text-2xl tracking-tighter text-white">FYB</Link>
          <a href="#enquire" className="btn-gold px-5 py-2 text-xs tracking-[0.15em] uppercase rounded-sm">Start The Conversation</a>
        </div>
      </nav>

      {/* HERO */}
      <header className="pt-28 pb-24 px-6">
        <div className="max-w-[1080px] mx-auto">
          <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-8">FYB &middot; Find Your Breakthrough</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl tracking-tighter uppercase text-white leading-[1.04] max-w-[16ch] mb-8">
            You were never addicted to the drink, the bet, or the gear. You were addicted to <span className="text-[#D4AF37]">spiking your nervous system.</span>
          </h1>
          <p className="font-heading text-xl sm:text-2xl lg:text-3xl uppercase text-white tracking-wide max-w-[26ch] mb-6 leading-tight">
            I know. I did it for forty years — and I climbed out.
          </p>
          <p className="text-zinc-300 text-base sm:text-lg max-w-[42ch] mb-10 leading-relaxed">
            I'm Merls. No clipboard. No textbook. No waiting room. Just a man who hit rock bottom and found the road back — and for thirty days, I walk it beside you.
          </p>
          <a href="#enquire" className="btn-gold px-10 py-5 text-sm tracking-[0.15em] uppercase rounded-sm inline-block">
            Start The Conversation
          </a>
        </div>
      </header>

      {/* BAND — "This was never going to come from a clinic" */}
      <section className="bg-[#121212] border-y border-white/10 py-20 sm:py-24 px-6">
        <div className="max-w-[720px] mx-auto space-y-6">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter uppercase text-white leading-[1.03]">
            This was never going to come <span className="text-[#D4AF37]">from a clinic.</span>
          </h2>
          <p className="text-zinc-300 text-lg leading-relaxed">
            You've maybe tried the rooms, the programs, the people reading from a manual who've never been within a mile of where you are. Or you've never gone near them, because none of it sounded like your life.
          </p>
          <p className="text-zinc-300 text-lg leading-relaxed">
            What pulled me out wasn't a diagnosis. It was understanding that I was never chasing the substance — I was chasing the spike. It was rebuilding my nervous system one day at a time. And it was faith — not as a worksheet, but as the thing that actually carried me when nothing else could.
          </p>
          <p className="text-white font-semibold text-lg leading-relaxed">
            That's what I bring. Not treatment. Lived experience, every single day, from a man who made it back.
          </p>
        </div>
      </section>

      {/* ACCESS — "For thirty days, you get me" */}
      <section className="py-20 sm:py-24 px-6">
        <div className="max-w-[1080px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-14 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter uppercase text-white leading-[1.02] mb-5">
                For thirty days, you don't get a program. <span className="text-[#D4AF37]">You get me.</span>
              </h2>
              <p className="text-zinc-300 text-lg leading-relaxed">
                For one month, Merls makes himself available — in the full capacity of who he is and what he's survived — exclusively to the few he takes on. No app to log into. No module to skip. A man, in your corner, every day, until the new way of living has roots.
              </p>
            </div>
            <div className="border border-[#D4AF37] p-2">
              <div className="divide-y divide-white/10">
                {[
                  { title: "Direct & Daily", desc: "Real contact with Merls, every day for thirty days — not a chatbot, not a workbook." },
                  { title: "The Nervous-System Work", desc: "The exact system he used to stop spiking and start training. Biology first." },
                  { title: "Faith, Full Strength", desc: "If you want it. It's what carried him, and he won't hide it or water it down." },
                  { title: "Four People. That's the Cap.", desc: "Because it's him, every day, in full. When the four spots are gone, they're gone." },
                ].map((item) => (
                  <div key={item.title} className="py-5 pl-9 pr-4 relative text-zinc-300 text-base">
                    <ArrowRight className="w-4 h-4 text-[#D4AF37] absolute left-2 top-6" />
                    <span className="font-heading text-sm tracking-[0.08em] uppercase text-white block mb-1">{item.title}</span>
                    {item.desc}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PATHS — Two ways in */}
      <section className="bg-[#121212] border-t border-white/10 py-20 sm:py-24 px-6">
        <div className="max-w-[1080px] mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4">Two Ways In</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter uppercase text-white">Who Are You Here For?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* For You */}
            <div className="border border-[#D4AF37] bg-[#171717] p-8 sm:p-10 space-y-5">
              <span className="inline-block font-heading text-xs tracking-[0.16em] uppercase bg-[#D4AF37] text-[#050505] px-3 py-1.5 font-bold">For You</span>
              <h3 className="font-heading text-2xl tracking-tight uppercase text-white">Merls 1:1 Recovery Protocol</h3>
              <p className="text-zinc-300 text-base leading-relaxed">
                For the person who's never been to rehab, won't go, or went and it didn't hold — but you're ready now. Thirty days, daily, just us. I'm not promising a cure in a month. I'm building the foundation recovery actually stands on: the daily operating system, the regulation, the faith, the man beside you who won't let you drift back.
              </p>
              <p className="text-zinc-500 text-sm italic mt-auto">If you're done spiking and ready to train — this is for you.</p>
            </div>
            {/* For Someone You Love */}
            <div className="border border-white/10 bg-[#171717] p-8 sm:p-10 space-y-5">
              <span className="inline-block font-heading text-xs tracking-[0.16em] uppercase bg-[#D4AF37] text-[#050505] px-3 py-1.5 font-bold">For Someone You Love</span>
              <h3 className="font-heading text-2xl tracking-tight uppercase text-white">Merls Family Protocol</h3>
              <p className="text-zinc-300 text-base leading-relaxed">
                Someone you love is in trouble — and you're the one reaching out for them. This is how you bring me in. I'll work with them directly, one on one — the same thirty days, the same daily presence — as a man who's been exactly where they are. One honest truth first: they have to be willing. If they've opened the door even a crack, I can meet them there and we can do real work. If they won't pick up the phone, there's only so much any of us can do — and I'll never pretend otherwise.
              </p>
              <p className="text-zinc-500 text-sm italic mt-auto">If you're ready to reach out for them — and they're willing to let someone in — start here.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PROMISE */}
      <section className="py-20 sm:py-24 px-6 text-center border-t border-white/10">
        <div className="max-w-[720px] mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter uppercase text-white leading-[1.03] mb-6">
            And if you need a doctor before you need <span className="text-[#D4AF37]">me</span> — I'll tell you straight.
          </h2>
          <p className="text-zinc-300 text-lg leading-relaxed">
            I only take people I know I can help. If what you need first is medical care or a detox, I'll say so on the call, and point you the right way. That's a promise, not a pitch.
          </p>
        </div>
      </section>

      {/* ENQUIRY FORM */}
      <section id="enquire" className="bg-[#121212] border-t border-white/10 py-20 sm:py-24 px-6">
        <div className="max-w-[1080px] mx-auto">
          <div className="text-center mb-4">
            <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4">No Pressure. Just a Conversation.</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter uppercase text-white">Tell Me Where Things Are At.</h2>
          </div>
          <p className="text-center text-zinc-300 text-lg max-w-[46ch] mx-auto mb-4">
            This goes straight to me — no call centre, no script. We talk, and together we work out whether I'm the right person for this.
          </p>
          <p className="text-center font-heading text-xl sm:text-2xl uppercase text-[#D4AF37] max-w-[24ch] mx-auto mb-10 leading-tight">
            Reaching out is the first rep. You don't need it figured out. You just need to start.
          </p>

          <div className="max-w-[640px] mx-auto bg-[#171717] border border-white/10 p-8 sm:p-10">
            {!submitted ? (
              <div className="space-y-6">
                {/* Toggle */}
                <div>
                  <p className="font-heading text-xs tracking-[0.1em] uppercase text-zinc-500 mb-4">I'm here for...</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setForWhom("me")}
                      className={`font-heading text-sm tracking-[0.04em] uppercase py-5 px-4 border transition-all text-center ${
                        forWhom === "me"
                          ? "bg-[#D4AF37] text-[#050505] border-[#D4AF37]"
                          : "bg-[#201E1A] text-white border-white/10 hover:border-[#D4AF37]"
                      }`}
                      data-testid="recovery-for-me"
                    >
                      Myself
                    </button>
                    <button
                      onClick={() => setForWhom("them")}
                      className={`font-heading text-sm tracking-[0.04em] uppercase py-5 px-4 border transition-all text-center ${
                        forWhom === "them"
                          ? "bg-[#D4AF37] text-[#050505] border-[#D4AF37]"
                          : "bg-[#201E1A] text-white border-white/10 hover:border-[#D4AF37]"
                      }`}
                      data-testid="recovery-for-them"
                    >
                      Someone I Love
                    </button>
                  </div>
                </div>

                {/* Fields */}
                <div className="space-y-5">
                  <div>
                    <label className="block font-heading text-sm tracking-[0.04em] uppercase text-white mb-2">First Name</label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="bg-[#201E1A] border-white/10 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                      data-testid="recovery-name-input"
                    />
                  </div>
                  <div>
                    <label className="block font-heading text-sm tracking-[0.04em] uppercase text-white mb-2">Email</label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="bg-[#201E1A] border-white/10 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                      data-testid="recovery-email-input"
                    />
                  </div>
                  <div>
                    <label className="block font-heading text-sm tracking-[0.04em] uppercase text-white mb-2">Phone</label>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="bg-[#201E1A] border-white/10 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                      data-testid="recovery-phone-input"
                    />
                  </div>
                  <div>
                    <label className="block font-heading text-sm tracking-[0.04em] uppercase text-white mb-2">
                      {forWhom === "them"
                        ? "In your own words — what's happening with the person you love?"
                        : "In your own words — where are things at right now?"}
                    </label>
                    <textarea
                      value={form.story}
                      onChange={(e) => setForm({ ...form, story: e.target.value })}
                      placeholder="You don't need to get it perfect. Just tell me what's going on."
                      rows={5}
                      className="w-full bg-[#201E1A] border border-white/10 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none text-white placeholder:text-zinc-600 px-4 py-3 text-base leading-relaxed"
                      data-testid="recovery-story-input"
                    />
                  </div>
                </div>

                <p className="text-zinc-500 text-sm italic leading-relaxed">
                  Whatever you write here stays between us. Say as much or as little as you need to.
                </p>

                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full btn-gold h-14 rounded-none text-base tracking-[0.06em] uppercase"
                  data-testid="recovery-submit-button"
                >
                  {loading ? "Sending..." : "Send This To Merls"}
                </Button>

                <p className="text-zinc-600 text-sm italic text-center">
                  Investment is discussed personally, once we know we're a fit.
                </p>
              </div>
            ) : (
              <div className="text-center py-8 space-y-4" data-testid="recovery-success">
                <h3 className="font-heading text-2xl tracking-tight uppercase text-white">Got It. This Is With Me Now.</h3>
                <p className="text-zinc-300 text-base leading-relaxed">
                  I read every one of these myself. I'll be in touch personally to set up a time to talk.
                  Hang in there — you've already done the hard part by reaching out.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#050505] border-t border-white/10 py-16 px-6 text-center">
        <div className="max-w-[720px] mx-auto space-y-6">
          <p className="text-zinc-300 italic text-lg sm:text-xl">The porch light's on. It always was.</p>
          <p className="font-heading text-sm tracking-[0.08em] uppercase text-[#D4AF37] font-semibold">
            You don't drift into a better life. You train into one.
          </p>
          <p className="font-heading text-xs tracking-[0.16em] uppercase text-zinc-600">
            FYB &middot; Find Your Breakthrough
          </p>
        </div>
      </footer>
    </div>
  );
}
